import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { Hash, Plus, Compass, MessageSquare, Users as UsersIcon, Phone } from 'lucide-react'
import { useCallManager } from '@/lib/callManager'
import { usePresence } from '@/lib/presence'
import Avatar from '@/components/Avatar'

/**
 * Left rail for team chat. Two sections:
 *   1. CHANNELS — public named threads the user has joined.
 *   2. DIRECT MESSAGES — DMs and group chats the user is a participant of.
 *
 * Three independent queries (one per section + one for the DM/group
 * participants needed to compute display names). All poll at 30s.
 * Per-conversation unread counts are batched via Promise.all per
 * section. N+1 with workspace-realistic caps (~20 channels, ~20 DMs/
 * groups) is acceptable; flagged for RPC promotion if it becomes hot.
 *
 * Display name rules:
 *   - Channel: c.name (DB-stored, e.g. "general")
 *   - DM:      the other participant's full_name
 *   - Group:   comma-joined other participants' full_names; truncated
 *              to "A, B, C +N more" when N>3 others.
 *
 * Active conversation highlighted by matching activeRoute against the
 * sidebar entry — channels by name, DMs/groups by id.
 */

const POLL_MS = 30_000

interface JoinedChannel {
  id: string
  name: string
  unread: number
}

interface JoinedDMOrGroup {
  id: string
  type: 'dm' | 'group'
  displayName: string
  unread: number
  // Populated for DMs only — the other participant's profile id, used
  // by the row's "call this person" button. Already in the fetched
  // participants payload; surfaced here so a second round-trip isn't
  // needed at click time.
  otherUserId?: string
}

export type SidebarActiveRoute =
  | { kind: 'channel'; name: string }
  | { kind: 'dm' | 'group'; id: string }
  | null

function formatGroupName(otherNames: string[]): string {
  if (otherNames.length === 0) return 'Group chat'
  if (otherNames.length <= 3) return otherNames.join(', ')
  const head = otherNames.slice(0, 3).join(', ')
  const remaining = otherNames.length - 3
  return `${head} +${remaining} more`
}

export default function ChannelSidebar({
  activeRoute,
  onChannelSelect,
  onDMSelect,
  onGroupSelect,
  onBrowseChannels,
  onCreateChannel,
  onStartDM,
  onNewGroup,
  style,
}: {
  activeRoute: SidebarActiveRoute
  onChannelSelect: (channelName: string) => void
  onDMSelect: (conversationId: string) => void
  onGroupSelect: (conversationId: string) => void
  onBrowseChannels: () => void
  onCreateChannel: () => void
  onStartDM: () => void
  onNewGroup: () => void
  // Optional style override for the outer container. Used by TeamChat to
  // re-shape the sidebar into a mobile drawer (full width, no inline
  // borderRight) without forking the component.
  style?: React.CSSProperties
}) {
  const { workspace, profile } = useAuth()
  const { state: callState, startCall } = useCallManager()
  const callBusy = callState.kind !== 'idle' && callState.kind !== 'ended'
  const { onlineUserIds } = usePresence()

  // ─── Channels (existing) ──────────────────────────────────────────────
  const { data: channels = [], isLoading: chLoading } = useQuery<JoinedChannel[]>({
    queryKey: ['team-joined-channels', workspace?.id, profile?.id],
    enabled: !!workspace?.id && !!profile?.id,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      if (!workspace?.id || !profile?.id) return []
      const { data: rows, error } = await supabase
        .from('team_conversation_participants')
        .select('team_conversation_id, last_read_at, team_conversations!inner(id, name, workspace_id, type)')
        .eq('user_id', profile.id)
        .eq('team_conversations.workspace_id', workspace.id)
        .eq('team_conversations.type', 'channel')
      if (error) throw error
      const joined = (rows || []) as any[]
      if (joined.length === 0) return []

      const counts = await Promise.all(
        joined.map(async row => {
          const since = row.last_read_at || new Date(0).toISOString()
          const { count } = await supabase
            .from('team_messages')
            .select('*', { count: 'exact', head: true })
            .eq('team_conversation_id', row.team_conversation_id)
            .gt('created_at', since)
            .neq('user_id', profile.id)
          return count || 0
        })
      )

      return joined.map((row, i) => ({
        id: row.team_conversation_id,
        name: row.team_conversations.name as string,
        unread: counts[i],
      }))
    },
  })

  // ─── DMs + groups ─────────────────────────────────────────────────────
  // Three steps: (1) my participations in non-channel convs;
  // (2) all participants of those convs with profile names so we can
  // build display names; (3) parallel unread COUNT per conversation.
  const { data: dmsGroups = [], isLoading: dgLoading } = useQuery<JoinedDMOrGroup[]>({
    queryKey: ['team-joined-dms-groups', workspace?.id, profile?.id],
    enabled: !!workspace?.id && !!profile?.id,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      if (!workspace?.id || !profile?.id) return []

      // (1) Find DMs/groups I'm in
      const { data: myRows, error: myErr } = await supabase
        .from('team_conversation_participants')
        .select('team_conversation_id, last_read_at, team_conversations!inner(id, type, workspace_id)')
        .eq('user_id', profile.id)
        .eq('team_conversations.workspace_id', workspace.id)
        .in('team_conversations.type', ['dm', 'group'])
      if (myErr) throw myErr
      const myJoined = (myRows || []) as any[]
      if (myJoined.length === 0) return []

      // (2) Fetch all participants + profile names for those conversations
      const ids = myJoined.map(r => r.team_conversation_id)
      const { data: allParts, error: pErr } = await supabase
        .from('team_conversation_participants')
        .select('team_conversation_id, user_id, profiles!team_conversation_participants_user_fkey(id, full_name)')
        .in('team_conversation_id', ids)
      if (pErr) throw pErr

      // Group participants by conversation
      const byConv: Record<string, Array<{ user_id: string; full_name: string | null }>> = {}
      ;(allParts || []).forEach((p: any) => {
        const cid = p.team_conversation_id
        if (!byConv[cid]) byConv[cid] = []
        byConv[cid].push({ user_id: p.user_id, full_name: p.profiles?.full_name ?? null })
      })

      // (3) Unread counts in parallel
      const counts = await Promise.all(
        myJoined.map(async row => {
          const since = row.last_read_at || new Date(0).toISOString()
          const { count } = await supabase
            .from('team_messages')
            .select('*', { count: 'exact', head: true })
            .eq('team_conversation_id', row.team_conversation_id)
            .gt('created_at', since)
            .neq('user_id', profile.id)
          return count || 0
        })
      )

      return myJoined.map((row, i) => {
        const parts = byConv[row.team_conversation_id] || []
        const others = parts.filter(p => p.user_id !== profile.id)
        const type = row.team_conversations.type as 'dm' | 'group'
        const displayName =
          type === 'dm'
            ? (others[0]?.full_name || 'Direct message')
            : formatGroupName(others.map(o => o.full_name || 'Unknown user'))
        return {
          id: row.team_conversation_id,
          type,
          displayName,
          unread: counts[i],
          otherUserId: type === 'dm' ? others[0]?.user_id : undefined,
        }
      })
    },
  })

  const sortedChannels = useMemo(() => {
    return [...channels].sort((a, b) => {
      if (a.name === 'general') return -1
      if (b.name === 'general') return 1
      return a.name.localeCompare(b.name)
    })
  }, [channels])

  const sortedDMsGroups = useMemo(() => {
    // Groups grouped above DMs? Or unread-first? Use: unread first
    // (descending), then alpha by display name. Mirrors Slack's "your
    // active DMs first" instinct.
    return [...dmsGroups].sort((a, b) => {
      if ((b.unread > 0 ? 1 : 0) - (a.unread > 0 ? 1 : 0) !== 0) {
        return (b.unread > 0 ? 1 : 0) - (a.unread > 0 ? 1 : 0)
      }
      return a.displayName.localeCompare(b.displayName)
    })
  }, [dmsGroups])

  const isChannelActive = (name: string) =>
    activeRoute?.kind === 'channel' && activeRoute.name === name
  const isDMOrGroupActive = (id: string) =>
    (activeRoute?.kind === 'dm' || activeRoute?.kind === 'group') && activeRoute.id === id

  return (
    <div style={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-subtle)', background: 'var(--surface)', overflow: 'hidden', ...style }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* CHANNELS */}
        <div style={{ padding: '18px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Channels</p>
          <button
            onClick={onBrowseChannels}
            aria-label="Browse channels"
            title="Browse channels"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <Compass style={{ width: 14, height: 14 }} />
          </button>
        </div>
        <div style={{ padding: '0 8px 8px' }}>
          {chLoading && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', padding: '4px 8px' }}>Loading…</p>}
          {!chLoading && sortedChannels.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', padding: '4px 8px' }}>No channels.</p>
          )}
          {sortedChannels.map(c => {
            const isActive = isChannelActive(c.name)
            return (
              <button
                key={c.id}
                onClick={() => onChannelSelect(c.name)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', marginBottom: 2,
                  background: isActive ? 'var(--accent-dim)' : 'transparent',
                  border: 'none', borderRadius: 8, cursor: 'pointer',
                  color: isActive ? 'var(--accent-text)' : 'var(--ink-secondary)',
                  fontSize: 13, fontWeight: isActive ? 600 : 500,
                  textAlign: 'left', transition: 'background 120ms ease',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--hover-bg)' }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
              >
                <Hash style={{ width: 14, height: 14, flexShrink: 0, opacity: 0.7 }} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                {c.unread > 0 && (
                  <span style={{ background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: 100, padding: '1px 7px', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                    {c.unread > 99 ? '99+' : c.unread}
                  </span>
                )}
              </button>
            )
          })}
          <button
            onClick={onCreateChannel}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 10px', marginTop: 4,
              background: 'transparent', border: 'none', borderRadius: 8,
              cursor: 'pointer', color: 'var(--ink-tertiary)',
              fontSize: 12, fontWeight: 500, textAlign: 'left',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.color = 'var(--accent-text)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-tertiary)' }}
          >
            <Plus style={{ width: 13, height: 13, flexShrink: 0 }} /> Create channel
          </button>
        </div>

        {/* DIRECT MESSAGES */}
        <div style={{ padding: '14px 16px 8px', borderTop: '1px solid var(--border-subtle)', marginTop: 4 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Direct messages</p>
        </div>
        <div style={{ padding: '0 8px 12px' }}>
          {dgLoading && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', padding: '4px 8px' }}>Loading…</p>}
          {!dgLoading && sortedDMsGroups.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', padding: '4px 8px' }}>No direct messages yet.</p>
          )}
          {sortedDMsGroups.map(c => {
            const isActive = isDMOrGroupActive(c.id)
            const Icon = c.type === 'dm' ? MessageSquare : UsersIcon
            // Call affordance only on 1:1 DM rows. Groups/channels never
            // get one (no single "other person" to call).
            const showCall = c.type === 'dm' && !!c.otherUserId
            return (
              <div
                key={c.id}
                style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}
              >
                <button
                  onClick={() => (c.type === 'dm' ? onDMSelect(c.id) : onGroupSelect(c.id))}
                  style={{
                    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px',
                    background: isActive ? 'var(--accent-dim)' : 'transparent',
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                    color: isActive ? 'var(--accent-text)' : 'var(--ink-secondary)',
                    fontSize: 13, fontWeight: isActive ? 600 : 500,
                    textAlign: 'left', transition: 'background 120ms ease',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--hover-bg)' }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
                >
                  {c.type === 'dm' && c.otherUserId ? (
                    // 1:1 DM → the other person's avatar with a presence dot.
                    <Avatar name={c.displayName} size={20} variant="contact" online={onlineUserIds.has(c.otherUserId)} ringColor="var(--surface)" />
                  ) : (
                    // Group rows: per-user presence has no single subject, so no dot.
                    <Icon style={{ width: 14, height: 14, flexShrink: 0, opacity: 0.7 }} />
                  )}
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.displayName}</span>
                  {c.unread > 0 && (
                    <span style={{ background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: 100, padding: '1px 7px', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                      {c.unread > 99 ? '99+' : c.unread}
                    </span>
                  )}
                </button>
                {showCall && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); startCall(c.otherUserId as string, c.displayName) }}
                    disabled={callBusy}
                    aria-label={`Call ${c.displayName}`}
                    title={callBusy ? 'You’re already on a call' : `Call ${c.displayName}`}
                    style={{
                      background: 'none', border: 'none',
                      cursor: callBusy ? 'not-allowed' : 'pointer',
                      color: 'var(--ink-tertiary)', padding: 6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 6, flexShrink: 0,
                      opacity: callBusy ? 0.35 : 0.6,
                      transition: 'opacity 120ms ease, color 120ms ease',
                    }}
                    onMouseEnter={e => { if (!callBusy) { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = 'var(--ink-primary)' } }}
                    onMouseLeave={e => { if (!callBusy) { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = 'var(--ink-tertiary)' } }}
                  >
                    <Phone style={{ width: 14, height: 14 }} />
                  </button>
                )}
              </div>
            )
          })}
          <button
            onClick={onStartDM}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 10px', marginTop: 4,
              background: 'transparent', border: 'none', borderRadius: 8,
              cursor: 'pointer', color: 'var(--ink-tertiary)',
              fontSize: 12, fontWeight: 500, textAlign: 'left',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.color = 'var(--accent-text)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-tertiary)' }}
          >
            <Plus style={{ width: 13, height: 13, flexShrink: 0 }} /> Start DM
          </button>
          <button
            onClick={onNewGroup}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 10px', marginTop: 2,
              background: 'transparent', border: 'none', borderRadius: 8,
              cursor: 'pointer', color: 'var(--ink-tertiary)',
              fontSize: 12, fontWeight: 500, textAlign: 'left',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.color = 'var(--accent-text)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ink-tertiary)' }}
          >
            <Plus style={{ width: 13, height: 13, flexShrink: 0 }} /> New group
          </button>
        </div>
      </div>
    </div>
  )
}
